// ============ Interfaces ============

export interface User {
  id: number;
  username: string;
  is_admin: number;
  last_note_id: number | null;
  open_notes: number[];
  expanded_folders: (number | null)[];
  panel_width: number;
  created_at: string;
}

export interface Folder {
  id: number;
  user_id: number;
  name: string;
  sort_order: number;
  is_pinned: number;
  color: string | null;
  created_at: string;
}

export interface Note {
  id: number;
  folder_id: number | null;
  name: string;
  stream_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface NoteListItem {
  id: number;
  folder_id: number | null;
  name: string;
  created_at: string;
  updated_at: string;
}

const API_BASE = "/api";

// ============ Auth Functions ============

export async function login(username: string, password: string): Promise<User> {
  const res = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Login failed");
  }
  return res.json();
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/logout`, { method: "POST" });
}

export async function getCurrentUser(): Promise<User> {
  const res = await fetch(`${API_BASE}/me`);
  if (!res.ok) throw new Error("Not authenticated");
  return res.json();
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/me/password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Failed to change password");
  }
}

// ============ Admin Functions ============

export async function getUsers(): Promise<User[]> {
  const res = await fetch(`${API_BASE}/admin/users`);
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}

export async function createUser(
  username: string,
  password: string,
  isAdmin: boolean
): Promise<User> {
  const res = await fetch(`${API_BASE}/admin/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, is_admin: isAdmin }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Failed to create user");
  }
  return res.json();
}

export async function updateUserPassword(
  id: number,
  password: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/users/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error("Failed to update password");
}

export async function deleteUser(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/users/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Failed to delete user");
  }
}

// ============ Note Functions ============

export async function fetchNotes(): Promise<NoteListItem[]> {
  const res = await fetch(`${API_BASE}/notes`);
  if (!res.ok) throw new Error("Failed to fetch notes");
  return res.json();
}

export async function fetchNote(id: number): Promise<Note> {
  const res = await fetch(`${API_BASE}/notes/${id}`);
  if (!res.ok) throw new Error("Failed to fetch note");
  return res.json();
}

export async function createNote(
  name: string,
  streamUrl?: string,
  folderId?: number | null
): Promise<Note> {
  const res = await fetch(`${API_BASE}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, stream_url: streamUrl, folder_id: folderId }),
  });
  if (!res.ok) throw new Error("Failed to create note");
  return res.json();
}

export async function updateNote(
  id: number,
  data: { name?: string; stream_url?: string; folder_id?: number | null }
): Promise<Note> {
  const res = await fetch(`${API_BASE}/notes/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!res.ok) throw new Error("Failed to update note");
  return res.json();
}

export async function deleteNote(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/notes/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete note");
}

// ============ Stream Functions ============

export async function fetchStreamFrame(): Promise<string> {
  // Fetch latest cached frame from the server and convert to a data URL.
  const res = await fetch(`${API_BASE}/stream/frame`);
  if (!res.ok) throw new Error("No stream active");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function fetchStreamStatus(): Promise<{ active: boolean; lastFrameAt: number }> {
  const res = await fetch(`${API_BASE}/stream/status`);
  if (!res.ok) throw new Error("Failed to check stream status");
  return res.json();
}

// ============ Folder Functions ============

export async function fetchFolders(): Promise<Folder[]> {
  const res = await fetch(`${API_BASE}/folders`);
  if (!res.ok) throw new Error("Failed to fetch folders");
  return res.json();
}

export async function createFolder(name: string): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to create folder");
  return res.json();
}

export async function deleteFolder(id: number): Promise<{ deletedNoteIds: number[] }> {
  const res = await fetch(`${API_BASE}/folders/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete folder");
  return res.json();
}

export async function renameFolder(id: number, name: string): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to rename folder");
  return res.json();
}

export async function reorderFolders(folderIds: number[]): Promise<Folder[]> {
  const res = await fetch(`${API_BASE}/folders/reorder`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folder_ids: folderIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder folders");
  return res.json();
}

export async function togglePinFolder(id: number): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders/${id}/pin`, {
    method: "PUT",
  });
  if (!res.ok) throw new Error("Failed to toggle pin");
  return res.json();
}

export async function updateFolderColor(id: number, color: string | null): Promise<Folder> {
  const res = await fetch(`${API_BASE}/folders/${id}/color`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ color }),
  });
  if (!res.ok) throw new Error("Failed to update folder color");
  return res.json();
}

// ============ User Preferences ============

export async function setLastNote(noteId: number | null): Promise<void> {
  const res = await fetch(`${API_BASE}/me/last-note`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note_id: noteId }),
  });
  if (!res.ok) throw new Error("Failed to set last note");
}

export async function getOpenNotes(): Promise<number[]> {
  const res = await fetch(`${API_BASE}/me/open-notes`);
  if (!res.ok) throw new Error("Failed to get open notes");
  return res.json();
}

export async function setOpenNotes(noteIds: number[]): Promise<void> {
  const res = await fetch(`${API_BASE}/me/open-notes`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note_ids: noteIds }),
  });
  if (!res.ok) throw new Error("Failed to set open notes");
}

export async function getExpandedFolders(): Promise<(number | null)[]> {
  const res = await fetch(`${API_BASE}/me/expanded-folders`);
  if (!res.ok) throw new Error("Failed to get expanded folders");
  return res.json();
}

export async function setExpandedFolders(folderIds: (number | null)[]): Promise<void> {
  const res = await fetch(`${API_BASE}/me/expanded-folders`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folder_ids: folderIds }),
  });
  if (!res.ok) throw new Error("Failed to set expanded folders");
}

export async function setPanelWidth(width: number): Promise<void> {
  const res = await fetch(`${API_BASE}/me/panel-width`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ panel_width: width }),
  });
  if (!res.ok) throw new Error("Failed to set panel width");
}

// ============ PDF Export ============

export interface NoteWithCanvas {
  id: number;
  name: string;
  folderName: string;
  canvasData: string | null;
}

export async function fetchNotesWithCanvas(): Promise<NoteWithCanvas[]> {
  const res = await fetch(`${API_BASE}/notes/with-canvas`);
  if (!res.ok) throw new Error("Failed to fetch notes");
  return res.json();
}

export async function exportAllPdfs(
  notes: Array<{
    name: string;
    folderName: string;
    svg: string;
    width: number;
    height: number;
  }>
): Promise<Blob> {
  const res = await fetch(`${API_BASE}/export-all-pdfs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notes }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Failed to export PDFs");
  }
  return res.blob();
}

export async function exportNotePdf(
  noteId: number,
  svg: string,
  width: number,
  height: number,
  name: string
): Promise<Blob> {
  const res = await fetch(`${API_BASE}/notes/${noteId}/export-pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ svg, width, height, name }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || "Failed to export PDF");
  }
  return res.blob();
}
